import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  api,
  type CommandRow,
  type ConfigRevisionRow,
  type CustomerRow,
  type DeviceDetail as Detail,
  type Entitlement,
  type EventRow
} from './api'
import ConfigForm, { maskedFields, type Config } from './ConfigForm'
import Icon, { type IconName } from './Icons'
import { ConfirmModal, PromptModal } from './Modal'
import { StatePill, formatTime, relativeTime } from './ui'

/** Icon per remote command, so the row reads as actions rather than as a word list. */
const COMMAND_ICONS: Record<string, IconName> = {
  start: 'power',
  stop: 'x',
  restart: 'refresh',
  applyConfig: 'layers'
}

const COMMANDS = ['start', 'stop', 'restart', 'applyConfig'] as const

/**
 * Hide the password in an rtsp://user:pass@host URL, mirroring the client's own
 * maskRtsp. Camera URLs routinely carry credentials, and this view is the one most
 * likely to end up in a screenshot or a shared screen.
 */
function maskCredentials(url: string): string {
  return url.replace(/(\/\/[^/:@]*):[^/@]*@/, '$1:••••@')
}

/**
 * What is currently pushed, at a glance. The raw JSON is still one click away,
 * but reading a 60-key object to answer "where is this streaming to?" is work the
 * console should be doing instead.
 *
 * `reported` marks a config the device sent us rather than one we pushed: those
 * arrive already redacted, so revealing credentials is not on offer — there are
 * none left in the payload to reveal.
 */
function ConfigSummary({
  config,
  reported = false
}: {
  config: Config
  reported?: boolean
}): React.JSX.Element {
  const [showUrls, setShowUrls] = useState(false)

  const list = (key: string): Record<string, unknown>[] =>
    Array.isArray(config[key]) ? (config[key] as Record<string, unknown>[]) : []
  const on = (r: Record<string, unknown>): boolean => r.enabled !== false
  const text = (v: unknown): string => (typeof v === 'string' ? v : '')

  const destinations = list('destinations').filter(on)
  const cameras = list('cameras')
  const enabledCams = cameras.filter(on)
  const hasSecret = cameras.some((c) => /\/\/[^/:@]*:[^/@]*@/.test(text(c.url)))

  return (
    <>
      <div className="summary">
        <div>
          <span>Streaming to</span>
          <strong>
            {destinations.length
              ? destinations.map((d) => text(d.name) || text(d.kind)).join(', ')
              : 'nowhere — no destination enabled'}
          </strong>
        </div>
        <div>
          <span>Cameras</span>
          <strong>
            {enabledCams.length} enabled
            {cameras.length > enabledCams.length && ` · ${cameras.length - enabledCams.length} off`}
          </strong>
        </div>
        <div>
          <span>Output</span>
          <strong>
            {String(config.outputWidth ?? '?')}×{String(config.outputHeight ?? '?')} @{' '}
            {String(config.fps ?? '?')} fps · {String(config.videoBitrateKbps ?? '?')} kb/s ·{' '}
            {text(config.encoder) || '?'}
          </strong>
        </div>
      </div>

      {cameras.length > 0 && (
        <div className="cam-list">
          <div className="cam-head">
            <h3>Sources</h3>
            <div className="spacer" />
            {hasSecret && !reported && (
              <button className="small" onClick={() => setShowUrls((v) => !v)}>
                <Icon name={showUrls ? 'eyeOff' : 'eye'} />
                {showUrls ? 'Hide credentials' : 'Show credentials'}
              </button>
            )}
          </div>

          {cameras.map((c, i) => {
            const kind = text(c.kind) || 'rtsp'
            const device = kind === 'device'
            const file = kind === 'file' || kind === 'picture'
            const online = kind === 'url'
            const url = text(c.url)
            return (
              <div className={`cam-row ${on(c) ? '' : 'off'}`} key={text(c.id) || i}>
                <Icon
                  name={device ? 'camera' : file ? 'layers' : online ? 'broadcast' : 'video'}
                  size={14}
                />
                <div className="cam-body">
                  <strong>{text(c.name) || `Camera ${i + 1}`}</strong>
                  <code className="mono">
                    {device
                      ? text(c.device) || '(no device selected)'
                      : file
                        ? // A path carries no credentials, so it is never masked.
                          url || '(no file chosen)'
                        : url
                          ? // A reported URL is already redacted by the device; masking
                            // it again would just stack a second row of dots on the first.
                            showUrls || reported
                            ? url
                            : maskCredentials(url)
                          : '(no URL set)'}
                  </code>
                </div>
                {file && <span className="badge">file</span>}
                {online && <span className="badge">online</span>}
                {!on(c) && <span className="badge">disabled</span>}
              </div>
            )
          })}

          {reported && (
            <p className="hint">
              Reported by the device, so credentials are already stripped. Nothing has been
              pushed from here yet.
            </p>
          )}
        </div>
      )}
    </>
  )
}

export default function DeviceDetail({
  deviceId,
  onBack
}: {
  deviceId: number
  onBack: () => void
}): React.JSX.Element {
  const [device, setDevice] = useState<Detail | null>(null)
  const [revisions, setRevisions] = useState<ConfigRevisionRow[]>([])
  const [commands, setCommands] = useState<CommandRow[]>([])
  const [events, setEvents] = useState<EventRow[]>([])
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const [draft, setDraft] = useState('')
  const [draftNote, setDraftNote] = useState('')
  const [editing, setEditing] = useState(false)
  const [draftError, setDraftError] = useState<string | null>(null)

  const [renaming, setRenaming] = useState(false)
  const [confirmRevoke, setConfirmRevoke] = useState(false)
  const [confirmRollback, setConfirmRollback] = useState<number | null>(null)
  /** Open when the operator is setting the licence date. */
  const [licensing, setLicensing] = useState(false)
  /** Accounts this device could belong to. Null until known, and for the
      operators who are not staff and are refused the list. */
  const [customers, setCustomers] = useState<CustomerRow[] | null>(null)

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const [detail, log] = await Promise.all([
        api.getDevice(deviceId),
        api.listEvents(deviceId, 100)
      ])
      setDevice(detail.device)
      setRevisions(detail.revisions)
      setCommands(detail.commands)
      setEvents(log.events)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }, [deviceId])

  useEffect(() => {
    void refresh()
    const timer = setInterval(() => void refresh(), 15000)
    return () => clearInterval(timer)
  }, [refresh])

  // Fetched once, not on the 15s beat: the account list barely changes, and a
  // refused call must not repaint an error every fifteen seconds.
  useEffect(() => {
    void api
      .listCustomers()
      .then((c) => setCustomers(c.customers))
      // Being refused the list is the correct answer for a customer's own
      // operator — they cannot reassign devices, so they get no picker.
      .catch(() => setCustomers(null))
  }, [])

  /** Runs an action, then refreshes — the pattern the other buttons here use. */
  const act = async (fn: () => Promise<unknown>): Promise<void> => {
    try {
      await fn()
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  /**
   * The draft is held as text because that is what setDesiredConfig takes, and
   * keeping one representation means the push sends exactly what was edited. The
   * form reads this and writes back through writeDraft.
   */
  const parsedDraft = useMemo<Config | null>(() => {
    try {
      const value: unknown = JSON.parse(draft)
      return value && typeof value === 'object' && !Array.isArray(value)
        ? (value as Config)
        : null
    } catch {
      return null
    }
  }, [draft])

  const writeDraft = (next: Config): void => {
    setDraft(JSON.stringify(next, null, 2))
    setDraftError(null)
  }

  const beginEdit = (): void => {
    // Seed from the desired config; fall back to what the device reports so a
    // device that was configured locally can be adopted without retyping it.
    // Reported configs have their secrets masked, which the note makes explicit.
    const source = device?.desiredConfig ?? device?.reportedConfig ?? {}
    setDraft(JSON.stringify(source, null, 2))
    setDraftError(null)
    setEditing(true)
  }

  const push = async (): Promise<void> => {
    let parsed: Config
    try {
      parsed = JSON.parse(draft) as Config
    } catch {
      setDraftError('This is not valid JSON.')
      return
    }
    // Pushing a redacted value would set the mask itself as the stream key (or as
    // the camera's credentials) and take the device off air on its next restart.
    const masked = maskedFields(parsed)
    if (masked.length > 0) {
      setDraftError(
        `Still masked: ${masked.join(', ')}. Re-enter ${
          masked.length > 1 ? 'these' : 'this'
        } before pushing — the device would otherwise receive the mask as the real value.`
      )
      return
    }
    try {
      const result = await api.setDesiredConfig(deviceId, draft, draftNote)
      setNotice(`Pushed configuration v${result.version}.`)
      setEditing(false)
      setDraftNote('')
      await refresh()
    } catch (err) {
      setDraftError(err instanceof Error ? err.message : String(err))
    }
  }

  const send = async (action: string): Promise<void> => {
    try {
      await api.sendCommand(deviceId, action)
      setNotice(`Queued "${action}" — it runs on the device's next heartbeat.`)
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  const rollback = async (version: number): Promise<void> => {
    setConfirmRollback(null)
    try {
      const result = await api.rollbackConfig(deviceId, version)
      setNotice(`Rolled back to v${version}, pushed as v${result.version}.`)
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  const rename = async (name: string): Promise<void> => {
    setRenaming(false)
    try {
      await api.renameDevice(deviceId, name)
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  const setRevoked = async (revoked: boolean): Promise<void> => {
    setConfirmRevoke(false)
    try {
      await api.setDeviceRevoked(deviceId, revoked)
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  if (!device) {
    return (
      <div className="stack">
        <button onClick={onBack}>
          <Icon name="back" />
          All devices
        </button>
        {error ? <div className="banner error">{error}</div> : <p className="hint">Loading…</p>}
      </div>
    )
  }

  return (
    <div className="stack">
      <div className="card-head">
        <button onClick={onBack}>
          <Icon name="back" />
          All devices
        </button>
        <div className="spacer" />
        {/* Staff only, and only rendered once the list has arrived — a customer's
            own operator is refused it, which is not an error to report. Without
            this the device stays unassigned and no subscription applies to it,
            so it is the step that makes a sale mean anything. */}
        {customers !== null && (
          <select
            title="Which account owns this device"
            // Selects stretch to their row by default, which would put one
            // control across the width of the action bar.
            style={{ maxWidth: 260 }}
            value={device.customerId ?? 0}
            onChange={(e) =>
              void act(() => api.setDeviceCustomer(deviceId, Number(e.target.value)))
            }
          >
            <option value={0}>Unassigned</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        )}
        <button onClick={() => setRenaming(true)}>
          <Icon name="pencil" />
          Rename
        </button>
        {device.approved && !device.revoked && (
          <button
            className="small"
            title="Withhold configuration and commands without revoking the token"
            onClick={() => void api.setDeviceApproved(deviceId, false).then(refresh)}
          >
            <Icon name="ban" />
            Set pending
          </button>
        )}
        <button
          className="small"
          title={
            device.licenseUntil
              ? `Licence runs to ${new Date(String(device.licenseUntil)).toLocaleDateString()}`
              : 'No expiry set — this device never lapses'
          }
          onClick={() => setLicensing(true)}
        >
          <Icon name="clock" />
          {device.licenseUntil
            ? `Licence · ${String(device.licenseUntil).slice(0, 10)}`
            : 'Set licence'}
        </button>
        <button
          className={device.revoked ? '' : 'danger'}
          onClick={() => (device.revoked ? void setRevoked(false) : setConfirmRevoke(true))}
        >
          <Icon name={device.revoked ? 'check' : 'ban'} />
          {device.revoked ? 'Restore' : 'Revoke'}
        </button>
      </div>

      <PromptModal
        open={renaming}
        title="Rename device"
        label="Device name"
        initial={device.name}
        onCancel={() => setRenaming(false)}
        onSubmit={(name) => void rename(name)}
      />

      <PromptModal
        open={licensing}
        title="Licence expiry"
        label="Expires on (YYYY-MM-DD) — leave empty for no expiry"
        initial={device.licenseUntil ? String(device.licenseUntil).slice(0, 10) : ''}
        onCancel={() => setLicensing(false)}
        onSubmit={(until) => {
          setLicensing(false)
          void api
            .setDeviceLicense(deviceId, until.trim(), String(device.licenseNote ?? ''))
            .then(refresh)
        }}
      />

      {/* Revoking is the one action here the device itself cannot undo — it stops
          being able to sync, so it cannot be restored from its own end. */}
      <ConfirmModal
        open={confirmRevoke}
        title="Revoke this device?"
        danger
        confirmLabel="Revoke"
        body={`${device.name} will have its token rejected and will stop syncing. It keeps streaming on its current configuration until someone stops it locally. You can restore it from here.`}
        onCancel={() => setConfirmRevoke(false)}
        onConfirm={() => void setRevoked(true)}
      />

      <ConfirmModal
        open={confirmRollback !== null}
        title={`Roll back to v${confirmRollback}?`}
        confirmLabel="Roll back"
        body={`This pushes v${confirmRollback}'s settings as a new version. The device applies it on its next heartbeat and restarts if it is streaming.`}
        onCancel={() => setConfirmRollback(null)}
        onConfirm={() => void rollback(confirmRollback as number)}
      />

      <section className="card">
        <div className="card-head">
          <h2>{device.name}</h2>
          <StatePill state={device.streamState} />
        </div>

        {device.revoked && (
          <div className="banner error">
            This device is revoked — its token is rejected and it cannot sync.
          </div>
        )}
        {/*
          An expired licence is not a fault and not a revoke: the box is still
          ours, still reachable, still checking in — it is simply handed no
          configuration until the date moves. Said in those words so nobody
          goes hunting for a technical problem that is not there.
        */}
        {device.entitlement.rung !== 'full' && !device.revoked && (
          <LapsedBanner entitlement={device.entitlement} />
        )}
        {!device.approved && !device.revoked && (
          <div className="banner">
            <Icon name="alert" />
            <div>
              This device registered itself and is <strong>waiting for approval</strong>. It
              can check in, but is refused configuration and commands until you accept it.
              <div className="inline" style={{ marginTop: 8 }}>
                <button
                  className="primary small"
                  onClick={() => void api.setDeviceApproved(deviceId, true).then(refresh)}
                >
                  <Icon name="check" />
                  Approve this device
                </button>
              </div>
            </div>
          </div>
        )}
        {device.lastErrorText && <div className="banner error">{device.lastErrorText}</div>}

        <div className="metrics">
          <div className="metric">
            <span>Last seen</span>
            <strong>{relativeTime(device.lastSeenAt)}</strong>
          </div>
          <div className="metric">
            <span>Bitrate</span>
            <strong>
              {device.stats?.bitrateKbps ? `${Math.round(device.stats.bitrateKbps)}k` : '—'}
            </strong>
          </div>
          <div className="metric">
            <span>FPS</span>
            <strong>{device.stats?.fps ? Math.round(device.stats.fps) : '—'}</strong>
          </div>
          <div className="metric">
            <span>Config</span>
            <strong>
              v{device.reportedConfigVersion}
              {device.configPending && ` → v${device.desiredConfigVersion}`}
            </strong>
          </div>
        </div>

        <div className="inline wrap">
          {COMMANDS.map((action) => (
            <button key={action} onClick={() => void send(action)} disabled={device.revoked}>
              <Icon name={COMMAND_ICONS[action]} />
              {action}
            </button>
          ))}
        </div>
        <p className="hint">
          Commands are pulled by the device on its next heartbeat, so they take
          effect within a few seconds rather than instantly.
        </p>

        {notice && <div className="banner ok">{notice}</div>}
        {error && <div className="banner error">{error}</div>}
      </section>

      <section className="card">
        <div className="card-head">
          <h2>Configuration</h2>
          {!editing && <button onClick={beginEdit}>Edit and push</button>}
        </div>

        {editing ? (
          <>
            {parsedDraft === null ? (
              <div className="banner error">
                This device&apos;s stored configuration is not a JSON object, so the form cannot
                show it. It was most likely written by hand or by another tool.
              </div>
            ) : (
              <ConfigForm config={parsedDraft} onChange={writeDraft} />
            )}

            {draftError && <div className="banner error">{draftError}</div>}

            <div className="inline wrap">
              <input
                placeholder="Note — what changed and why"
                value={draftNote}
                onChange={(e) => setDraftNote(e.target.value)}
              />
              <button className="primary" onClick={() => void push()}>
                Push to device
              </button>
              <button onClick={() => setEditing(false)}>Cancel</button>
            </div>
            <p className="hint">
              Pushing bumps the version; the device applies it on its next
              heartbeat and reports back. If it is streaming, it restarts to make
              the change effective.
            </p>
          </>
        ) : device.desiredConfig ? (
          <>
            <ConfigSummary config={device.desiredConfig} />
            <details>
              <summary className="hint">Raw JSON</summary>
              <pre className="mono scroll">{JSON.stringify(device.desiredConfig, null, 2)}</pre>
            </details>
          </>
        ) : device.reportedConfig ? (
          /* Nothing pushed, but the device tells us what it is running — showing that
             is strictly better than showing nothing, and it is how you check a
             locally-configured device before adopting it. */
          <>
            <div className="banner">
              <Icon name="alert" />
              Nothing has been pushed from here. This is what the device reports it is
              running — <strong>Edit and push</strong> starts from it.
            </div>
            <ConfigSummary config={device.reportedConfig} reported />
            <details>
              <summary className="hint">Raw JSON (as reported)</summary>
              <pre className="mono scroll">{JSON.stringify(device.reportedConfig, null, 2)}</pre>
            </details>
          </>
        ) : (
          <p className="hint">
            Nothing has been pushed to this device yet, and it has not reported a
            configuration — it may not have completed a heartbeat since enrolling.
          </p>
        )}

        {revisions.length > 0 && (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Version</th>
                  <th>When</th>
                  <th>By</th>
                  <th>Note</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {revisions.map((r) => (
                  <tr key={r.version}>
                    <td>v{r.version}</td>
                    <td className="sub">{formatTime(r.createdAt)}</td>
                    <td className="sub">{r.authorEmail}</td>
                    <td className="sub">{r.note ?? '—'}</td>
                    <td>
                      {r.version !== device.desiredConfigVersion && (
                        <button className="small" onClick={() => setConfirmRollback(r.version)}>
                          Roll back
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="card">
        <h2>Recent commands</h2>
        {commands.length === 0 ? (
          <p className="hint">None yet.</p>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Action</th>
                  <th>Queued</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {commands.map((c) => (
                  <tr key={c.id}>
                    <td>{c.action}</td>
                    <td className="sub">{relativeTime(c.createdAt)}</td>
                    <td className="sub">
                      {c.ackedAt ? (
                        <span className={c.ok ? 'good' : 'err'}>
                          {c.ok ? '✓' : '✗'} {c.resultText}
                        </span>
                      ) : c.deliveredAt ? (
                        'delivered, awaiting result'
                      ) : (
                        'queued'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="card">
        <h2>Timeline</h2>
        {events.length === 0 ? (
          <p className="hint">Nothing recorded yet.</p>
        ) : (
          <div className="timeline">
            {events.map((e) => (
              <div key={e.id} className={`event ${e.level}`}>
                <span className="ts">{formatTime(e.ts)}</span>
                <span className="kind">{e.kind}</span>
                <span className="text">{e.text}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

/**
 * What a lapsed licence has actually done to this device — which is different
 * on each rung, and only ever "refused configuration" on the last one.
 *
 * Written this way because the failure mode is an operator reading "expired"
 * and going to look for a technical fault, or reading "refused configuration"
 * on day two when nothing has been refused at all. The banner says what is
 * true today and what happens next, and never says the device stopped
 * streaming — it never does.
 */
function LapsedBanner({ entitlement }: { entitlement: Entitlement }): React.JSX.Element {
  const on = entitlement.expiresAt
    ? new Date(entitlement.expiresAt).toLocaleDateString()
    : 'an earlier date'

  const body: Record<string, React.JSX.Element> = {
    warned: (
      <>
        The licence <strong>expired</strong> on {on}. Nothing has been withheld yet —
        exports stop after a week, and the device keeps receiving configuration until 30
        days past the date. Extending it now costs nothing and needs no visit.
      </>
    ),
    featuresWithheld: (
      <>
        The licence expired on {on}, so <strong>exports and reports are withheld</strong>.
        Streaming and recording are unaffected. Extending the date restores them on the
        next heartbeat.
      </>
    ),
    retentionCut: (
      <>
        The licence expired on {on}. New recordings are now kept for{' '}
        <strong>one day only</strong>, and exports are withheld. Footage already on disk
        has not been deleted. Configuration stops being served 30 days past the date.
      </>
    ),
    silent: (
      <>
        The licence <strong>expired</strong> on {on}. The device still checks in, but is
        refused configuration and commands until the date is extended — nothing has to be
        reinstalled or re-enrolled, and anything it is currently streaming keeps running.
      </>
    )
  }

  return (
    <div className={`banner ${entitlement.rung === 'warned' ? '' : 'error'}`}>
      <Icon name="alert" />
      <div>{body[entitlement.rung]}</div>
    </div>
  )
}
